import { useCallback, useState } from 'react';
import PullToRefresh from 'react-simple-pull-to-refresh';
import { MobileListHeader } from './MobileListHeader';
import { MobilePagination } from './MobilePagination';
import { MobileListProps } from './types';

/**
 * MobileList - Generic mobile list component
 * Renders a scrollable list of items optimized for touch interfaces
 */
export function MobileList<TData>({
  data,
  isLoading = false,
  onRefresh,
  getRowId,
  renderItem,
  enableSelection = false,
  selectedIds = [],
  onSelectionChange,
  onItemClick,
  title,
  icon,
  headerActions,
  emptyState,
  ariaLabel = 'List',
  currentPage,
  totalPages,
  totalCount,
  rowsPerPage,
  onPageChange,
}: MobileListProps<TData>) {
  const [selectionMode, setSelectionMode] = useState(false);

  // Handle item selection
  const handleSelect = useCallback(
    (id: string) => {
      if (!onSelectionChange) return;

      const isSelected = selectedIds.includes(id);
      const newSelectedIds = isSelected
        ? selectedIds.filter((selectedId) => selectedId !== id)
        : [...selectedIds, id];

      onSelectionChange(newSelectedIds);
    },
    [selectedIds, onSelectionChange]
  );

  // Handle item click
  const handleItemClick = useCallback(
    (item: TData) => {
      if (selectionMode) {
        // In selection mode, clicking selects/deselects
        handleSelect(getRowId(item));
      } else {
        // Normal mode, trigger onClick handler
        onItemClick?.(item);
      }
    },
    [selectionMode, handleSelect, onItemClick, getRowId]
  );

  // Toggle selection mode
  const toggleSelectionMode = useCallback(() => {
    if (selectionMode && onSelectionChange) {
      // Exiting selection mode, clear selections
      onSelectionChange([]);
    }
    setSelectionMode(!selectionMode);
  }, [selectionMode, onSelectionChange]);

  // Clear selection
  const handleClearSelection = useCallback(() => {
    if (onSelectionChange) {
      onSelectionChange([]);
    }
  }, [onSelectionChange]);

  // Loading state
  if (isLoading) {
    return (
      <div className='max-w-full bg-white' role='region' aria-label={ariaLabel}>
        {title && (
          <MobileListHeader
            title={title}
            icon={icon}
            headerActions={headerActions}
            enableSelection={enableSelection}
          />
        )}
        <div className='max-w-full divide-y divide-gray-200'>
          {[...Array(5)].map((_, i) => (
            <div key={i} className='flex animate-pulse items-center gap-4 p-4'>
              <div className='h-14 w-14 rounded bg-gray-200' />
              <div className='flex-1'>
                <div className='mb-2 h-4 w-3/4 rounded bg-gray-200' />
                <div className='h-3 w-1/2 rounded bg-gray-200' />
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  }

  // Empty state
  if (data.length === 0) {
    return (
      <div className='max-w-full bg-white' role='region' aria-label={ariaLabel}>
        {title && (
          <MobileListHeader
            title={title}
            icon={icon}
            headerActions={headerActions}
            enableSelection={enableSelection}
          />
        )}
        <div className='flex items-center justify-center py-12'>
          {emptyState || (
            <div className='text-center text-gray-500'>
              <p className='mb-1 text-lg font-medium'>No items</p>
              <p className='text-sm'>No data to display</p>
            </div>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className='max-w-full bg-white' role='region' aria-label={ariaLabel}>
      {/* Header */}
      {title && (
        <MobileListHeader
          title={title}
          icon={icon}
          headerActions={
            enableSelection ? (
              <>
                <button
                  onClick={toggleSelectionMode}
                  className={`hover:bg-blue-050 cursor-pointer rounded border
                    border-blue-600 px-3 py-1.5 text-sm font-medium
                    text-blue-600 transition-colors hover:text-blue-800`}
                >
                  {selectionMode ? 'Done' : 'Select'}
                </button>
                {headerActions}
              </>
            ) : (
              headerActions
            )
          }
          enableSelection={enableSelection}
          selectedIds={selectedIds}
          onClearSelection={selectionMode ? handleClearSelection : undefined}
        />
      )}

      {/* Pull-to-refresh wrapper for the list content */}
      {onRefresh ? (
        <PullToRefresh
          onRefresh={onRefresh}
          pullingContent={
            <div
              className='flex items-center justify-center py-2 text-sm
                text-gray-500'
            >
              ↓ Pull to refresh
            </div>
          }
          refreshingContent={
            <div
              className='flex items-center justify-center gap-2 py-2 text-sm
                text-gray-500'
            >
              <svg
                className='h-4 w-4 animate-spin'
                fill='none'
                viewBox='0 0 24 24'
              >
                <circle
                  className='opacity-25'
                  cx='12'
                  cy='12'
                  r='10'
                  stroke='currentColor'
                  strokeWidth='4'
                />
                <path
                  className='opacity-75'
                  fill='currentColor'
                  d='M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z'
                />
              </svg>
              Refreshing...
            </div>
          }
        >
          <div className='max-w-full divide-y divide-gray-200' role='list'>
            {data.map((item) => {
              const id = getRowId(item);
              const isSelected = selectedIds.includes(id);

              return (
                <div
                  key={id}
                  role='listitem'
                  className='max-w-full overflow-hidden'
                >
                  {renderItem(item, {
                    isSelected,
                    selectionMode,
                    onSelect: handleSelect,
                    onClick: handleItemClick,
                  })}
                </div>
              );
            })}
          </div>
        </PullToRefresh>
      ) : (
        <div className='max-w-full divide-y divide-gray-200' role='list'>
          {data.map((item) => {
            const id = getRowId(item);
            const isSelected = selectedIds.includes(id);

            return (
              <div
                key={id}
                role='listitem'
                className='max-w-full overflow-hidden'
              >
                {renderItem(item, {
                  isSelected,
                  selectionMode,
                  onSelect: handleSelect,
                  onClick: handleItemClick,
                })}
              </div>
            );
          })}
        </div>
      )}

      {/* Pagination footer (mobile) */}
      {typeof currentPage === 'number' &&
        typeof totalPages === 'number' &&
        onPageChange && (
          <div className='border-border-secondary border-t'>
            <MobilePagination
              currentPage={currentPage}
              totalPages={totalPages}
              totalCount={totalCount}
              rowsPerPage={rowsPerPage}
              onPageChange={onPageChange}
              isLoading={isLoading}
            />
          </div>
        )}
    </div>
  );
}
